import {
  AppwideDrawerContentType,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceProvider,
  useAppwideDrawerContext,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { closeView } from '../components/OMS/OMSSlice';
import { TransferFormDrawer } from '../containers/Transfers/TransferFormDrawer';
import type { TransferForm } from '../containers/Transfers/types';
import { useAppStateDispatch } from '../providers/AppStateProvider';

export const useTransfersDrawer = () => {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();

  const { openDrawer, closeDrawer, isOpen, contentSpec } = useAppwideDrawerContext();

  const openTransfersDrawer = useCallback(
    ({
      initialTransfer,
      source = MixpanelEventSource.TransferBlotter,
    }: { initialTransfer?: TransferForm; source?: MixpanelEventSource } = {}) => {
      mixpanel.track(MixpanelEvent.ToggleTransfersDrawer, {
        [MixpanelEventProperty.Source]: source,
        [MixpanelEventProperty.Expanded]: true,
      });

      dispatch(closeView());
      openDrawer({
        type: AppwideDrawerContentType.TransferForm,
        title: 'Transfer Form',
        renderContent: () => (
          <MixpanelSourceProvider value={source}>
            <TransferFormDrawer initialForm={initialTransfer} portalizeDropdowns={false} />
          </MixpanelSourceProvider>
        ),
      });
    },
    [dispatch, openDrawer, mixpanel]
  );

  const closeTransfersDrawer = useCallback(
    (source?: MixpanelEventSource) => {
      if (isOpen && contentSpec?.type === AppwideDrawerContentType.TransferForm) {
        mixpanel.track(MixpanelEvent.ToggleTransfersDrawer, {
          [MixpanelEventProperty.Source]: source,
          [MixpanelEventProperty.Expanded]: false,
        });
        closeDrawer();
      }
    },
    [closeDrawer, mixpanel, isOpen, contentSpec?.type]
  );

  const toggleTransfersDrawer = useCallback(
    (source?: MixpanelEventSource) => {
      if (isOpen && contentSpec?.type === AppwideDrawerContentType.TransferForm) {
        closeTransfersDrawer();
      } else {
        openTransfersDrawer();
      }
    },
    [isOpen, openTransfersDrawer, closeTransfersDrawer, contentSpec?.type]
  );

  return {
    isTransfersDrawerOpen: isOpen && contentSpec?.type === AppwideDrawerContentType.TransferForm,
    openTransfersDrawer,
    closeTransfersDrawer,
    toggleTransfersDrawer,
  };
};
